import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { transformHumanTask } from "../transforms/humanTask";
import type { DecisionItem } from "../transforms/humanTask";
import { HUMAN_TASK_QUEUE_LIMIT } from "../constants/humanTask";

export interface ApiError {
  message: string;
  code: string;
}

export type HumanTaskQueueState =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "ready"; data: DecisionItem[]; count: number }
  | { status: "error"; error: ApiError }
  | { status: "forbidden" };

/**
 * Reactive named hook for the Decisions tray.
 *
 * Returns a discriminated union representing the current state of the
 * signed-in user's visible (non-snoozed) human-task queue.
 *
 * Passes the sentinel value "skip" to useQuery when the subscription
 * scope is inactive (e.g., during workspace navigation transitions),
 * preventing a network round-trip.
 *
 * The "error" and "forbidden" states in HumanTaskQueueState are not
 * returned by this hook at runtime. When useQuery throws a ConvexError,
 * React propagates it to the nearest error boundary during render. Those
 * states exist in the type union for unit-test injection via mock hook
 * factories. The DecisionsTrayErrorBoundary handles the visual for those
 * states at runtime.
 */
export function useHumanTaskQueue(): HumanTaskQueueState {
  const isActive = useSubscriptionActive();

  // When isActive is false, "skip" suspends the subscription without a
  // network call. This is the canonical Convex pattern for conditional
  // subscriptions.
  const result = useQuery(
    api.sessions.queries.subscribeToHumanTasksByUser,
    isActive ? { limit: HUMAN_TASK_QUEUE_LIMIT } : "skip",
  );

  if (!isActive || result === undefined) {
    return { status: "loading" };
  }

  if (result.length === 0) {
    return { status: "empty" };
  }

  return {
    status: "ready",
    data: result.map(transformHumanTask),
    count: result.length,
  };
}
